import { useEffect, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { hooks as commonHooks } from '@sitebuilder/common';

const range = (start: number, end: number) => {
    const length = end - start + 1;
    /*
      Creates an array of certain length and set the elements within it from
      start value to end value.
    */
    return Array.from({ length }, (_, idx) => idx + start);
};

interface PaginationProps {
    totalCount: number;
    pageSize: number;
    currentPage: number;
    pazeSizeOptions: number[];
    onChangePageSize: (e: any) => void;
    showPageSizeDropDown: boolean;
    onPageChange: (d: number) => void;
    siblingCount?: number;
    showCurrentPageText?: boolean;
    pagingType: string;
    enableShowAll: boolean;
    showAllText: string;
    pagePickerPosition: string;
    pageSizePickerPosition: string;
    currentPageTextPosition: string;
    pagerButtonStyle: string;
    currentPageText: string;
    previousBtnText: string;
    nextBtnText: string;
    firstBtnText: string;
    lastBtnText: string;
    pageSizePickerText: string;
    pagePickerDropdownImage: string;
    staticDataTabIndex: number;
    showAllRows: boolean;
    setShowAllRows: (e: boolean) => void;
}

const usePagintion = ({
    totalCount,
    pageSize,
    siblingCount,
    currentPage,
}: {
    totalCount: number;
    pageSize: number;
    siblingCount: number;
    currentPage: number;
}) => {
    const paginationRange = useMemo(() => {
        const totalPageCount = Math.ceil(totalCount / pageSize);

        // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
        const totalPageNumbers = siblingCount + 5;

        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

        /* 
            We do not show dots just when there is just one page number to be inserted between
            the extremes of sibling and the page limits i.e 1 and totalPageCount.
            Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2 
        */
        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPageCount;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = range(1, leftItemCount);

            return [...leftRange, 'DOTS', totalPageCount];
        } else if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
            return [firstPageIndex, 'DOTS', ...rightRange];
        } else {
            const middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, 'DOTS', ...middleRange, 'DOTS', lastPageIndex];
        }
    }, [totalCount, pageSize, siblingCount, currentPage]);

    return paginationRange;
};

export const Pagination = (props: PaginationProps) => {
    const {
        onPageChange,
        totalCount,
        currentPage,
        pageSize,
        pazeSizeOptions,
        onChangePageSize,
        showPageSizeDropDown,
        siblingCount = 1,
        showCurrentPageText,
        pagePickerPosition,
        pageSizePickerPosition,
        currentPageTextPosition,
        pagingType,
        enableShowAll,
        showAllText,
        pagerButtonStyle,
        pagePickerDropdownImage,
        showAllRows,
        setShowAllRows,
        ...rest
    } = props;

    useEffect(() => {
        if (showAllRows && pageSize !== totalCount) {
            onChangePageSize(totalCount);
        }
    }, [showAllRows, onChangePageSize, pageSize, totalCount]);

    const paginationRange = usePagintion({
        currentPage,
        totalCount,
        siblingCount,
        pageSize,
    });
    const i18n = commonHooks.useLocalization();

    const pagingTypeMap = {
        firstLast: ['full', 'full_numbers', 'first_last_numbers'],
        prevNext: ['simple', 'simple_numbers', 'full', 'full_numbers'],
        number: ['numbers', 'simple_numbers', 'full_numbers', 'first_last_numbers'],
    };

    const positionsMap: any = {
        left: [],
        center: [],
        right: [],
    };

    if (showCurrentPageText) {
        positionsMap[currentPageTextPosition].push('currentPageText');
    }

    if (showPageSizeDropDown) {
        positionsMap[pageSizePickerPosition].push('pageSizePicker');
    }

    positionsMap[pagePickerPosition].push('pagePicker');

    const lastPage = paginationRange[paginationRange?.length - 1];

    const onNext = () => {
        onPageChange(currentPage + 1);
    };

    const onPrevious = () => {
        onPageChange(currentPage - 1);
    };

    const onFirst = () => {
        onPageChange(1);
    };

    const onLast = () => {
        typeof lastPage === 'number' && onPageChange(lastPage);
    };

    const CurrentPageText = () => {
        return (
            <p
                className={'current-page-text'}
                data-testid={'current-page-text'}
                tabIndex={rest.staticDataTabIndex}
            >
                {rest.currentPageText}
            </p>
        );
    };

    const PageSizePicker = () => {
        return (
            <div className="page-size-select" data-testid={'page-size-select'}>
                <span className="page-size-text" tabIndex={rest.staticDataTabIndex}>
                    {rest.pageSizePickerText || i18n.t('pagination_rows_per_page')}
                </span>
                <label htmlFor={'page-size-dropdown'} className="sr-only">
                    {'page size selector'}
                </label>
                <Dropdown
                    onSelect={(e: any) => {
                        onChangePageSize(e);
                        setShowAllRows(e === totalCount.toString());
                    }}
                    className="filter-dropdown"
                >
                    <Dropdown.Toggle variant="secondary" id="page-size-dropdown">
                        {pageSize} <img src={pagePickerDropdownImage} alt="Icon" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {pazeSizeOptions.map((a: number, i: number) => (
                            <Dropdown.Item key={i} eventKey={a}>
                                {a}
                            </Dropdown.Item>
                        ))}

                        {enableShowAll && (
                            <Dropdown.Item eventKey={totalCount}>{showAllText}</Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const PagePicker = () => {
        return (
            <ul
                className={`pagination-container ${
                    pagerButtonStyle === 'circle' ? 'circle-buttons' : 'rect-buttons'
                }`}
                data-testid={'pagination-container'}
            >
                {/* First Button */}
                {pagingTypeMap.firstLast.includes(pagingType) && (
                    <li
                        className={`pagination-item ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={onFirst}
                        onKeyDown={(e) => (e.key === 'Enter' ? onFirst() : null)}
                        tabIndex={currentPage === 1 ? -1 : 0}
                        aria-hidden={currentPage === 1}
                    >
                        <div className="arrow left" />
                        <div className="arrow left" />
                        <span>{rest.firstBtnText || i18n.t('pagination_first')}</span>
                    </li>
                )}
                {/* Previous Button */}
                {pagingTypeMap.prevNext.includes(pagingType) && (
                    <li
                        className={`pagination-item ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={onPrevious}
                        onKeyDown={(e) => (e.key === 'Enter' ? onPrevious() : null)}
                        tabIndex={currentPage === 1 ? -1 : 0}
                        aria-hidden={currentPage === 1}
                    >
                        <div className="arrow left" />
                        <span>{rest.previousBtnText || i18n.t('pagination_previous')}</span>
                    </li>
                )}
                {/* Page Numbers */}
                {pagingTypeMap.number.includes(pagingType) &&
                    paginationRange.map((pageNumber, ind) => {
                        // If the pageItem is a DOT, render the DOTS unicode character
                        if (pageNumber === 'DOTS') {
                            return (
                                <li key={ind} className="pagination-item dots" tabIndex={0}>
                                    &#8230;
                                </li>
                            );
                        }
                        return (
                            <li
                                key={ind}
                                className={`pagination-item pagination-number-button ${
                                    pageNumber === currentPage ? 'selected' : ''
                                }`}
                                data-testid="pagination-number-button"
                                onClick={() =>
                                    typeof pageNumber === 'number' && onPageChange(pageNumber)
                                }
                                onKeyDown={(e) =>
                                    e.key === 'Enter'
                                        ? typeof pageNumber === 'number' && onPageChange(pageNumber)
                                        : null
                                }
                                tabIndex={0}
                            >
                                {pageNumber}
                            </li>
                        );
                    })}
                {/* Next Button */}
                {pagingTypeMap.prevNext.includes(pagingType) && (
                    <li
                        className={`pagination-item ${currentPage === lastPage ? 'disabled' : ''}`}
                        onClick={onNext}
                        onKeyDown={(e) => (e.key === 'Enter' ? onNext() : null)}
                        tabIndex={currentPage === lastPage ? -1 : 0}
                        aria-hidden={currentPage === lastPage}
                    >
                        <span>{rest.nextBtnText || i18n.t('pagination_next')}</span>
                        <div className="arrow right" />
                    </li>
                )}
                {/* Last Button */}
                {pagingTypeMap.firstLast.includes(pagingType) && (
                    <li
                        className={`pagination-item ${currentPage === lastPage ? 'disabled' : ''}`}
                        onClick={onLast}
                        onKeyDown={(e) => (e.key === 'Enter' ? onLast() : null)}
                        tabIndex={currentPage === lastPage ? -1 : 0}
                        aria-hidden={currentPage === lastPage}
                    >
                        <span>{rest.lastBtnText || i18n.t('pagination_last')}</span>
                        <div className="arrow right" />
                        <div className="arrow right" />
                    </li>
                )}
            </ul>
        );
    };

    const renderPaginationComponent = (d: any, ind: number) => {
        if (d === 'currentPageText') {
            return (
                <div key={d + ind}>
                    <CurrentPageText />
                </div>
            );
        } else if (d === 'pageSizePicker') {
            return (
                <div key={d + ind}>
                    <PageSizePicker />
                </div>
            );
        } else {
            return (
                <div key={d + ind}>
                    <PagePicker />
                </div>
            );
        }
    };

    return (
        <div className="pagination-component">
            {['left', 'center', 'right'].map((key: any, i) => (
                <div className={`pagination-component-${key}`} key={key + i}>
                    {positionsMap[key].length
                        ? positionsMap[key].map((d: any, ind: number) =>
                              renderPaginationComponent(d, ind)
                          )
                        : ''}
                </div>
            ))}
        </div>
    );
};

export default Pagination;
